/**
 * The immersive session: getting into a headset, and being somewhere once you
 * are in one.
 *
 * Everything here is additive and every path through it is gated on an actual
 * WebXR session existing. On a desktop browser with no headset,
 * `navigator.xr.isSessionSupported` resolves false for every mode, `available`
 * stays false, no DOM is created, no rig is built, no listener is attached and
 * nothing at all is done per frame. That is the same rule `net/client.ts` plays
 * by and it is checked the same way: the flat game must be indistinguishable
 * from the build that had none of this in it.
 *
 * ## Two headsets, and not assuming which one this is
 *
 * A Quest offers `immersive-vr`. Apple's own announcement of WebXR in Safari on
 * visionOS 2 says the same — `immersive-vr` sessions, with the AR module not
 * enabled — and Apple's developer forums say plainly that `immersive-ar` is not
 * in a working state on visionOS even with the flag on. So on paper both target
 * devices want the same request.
 *
 * On paper is not a headset, and neither device is in this room. So the entry
 * path is a ladder rather than a call: every mode the browser actually claims,
 * tried in order of how much of the design survives it, each one first with the
 * optional features and then without them. A refused optional feature must
 * never cost the session — asking for hand tracking and being told no should
 * leave the player in the building without hands, not in their kitchen with an
 * error. `mode` reports which session was obtained.
 *
 * If what comes back is `immersive-ar`, the passthrough is the problem rather
 * than the feature, and `render/xrenv.ts` shuts it out. See that file: it is not
 * something `scene.background` can do.
 *
 * What a session changes, and why each one is unavoidable:
 *
 *  - **The camera is the headset.** three owns the camera pose inside a
 *    session; what the game owns is the *rig* the camera sits in.
 *    `WebXRManager.updateCamera` composes the head pose with `camera.parent`'s
 *    world matrix, so parenting the camera to a `Group` and moving that group
 *    is the whole of locomotion. `render/renderer.ts` stops writing
 *    `camera.position` and `camera.rotation` and writes the rig instead.
 *  - **`PlayerState` is read back off the head.** Yaw, pitch and — the one that
 *    matters for play — `eyeHeight` all come from where the player's head
 *    actually is. Ducking behind a desk is real, which matters because getting
 *    low is the counterplay to the only entity that reliably kills.
 *  - **Head bob is off.** It is nauseating in stereo and the headset already
 *    knows where the head is; a second, invented head movement laid over the
 *    real one is exactly the mismatch that makes people ill.
 *  - **The post chain is bypassed.** See `xrvignette.ts`.
 *
 * The reference space is `local-floor` and it is asked for as *required*: this
 * design reads the player's standing height straight out of it, and a session
 * measured from the headset's boot position instead of from the floor would put
 * their eyes on the carpet. The bottom rung of the ladder gives even that up
 * and assumes a standing height instead, because a session with a guessed floor
 * is still a session and no session at all is not. Nothing on either target
 * device is expected to get that far; see `ASSUMED_EYE`.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
// The same seam the on-screen touch controls feed. Type only: nothing in the
// render layer constructs or owns the input manager.
import type { InputSink } from '../core/input';
import { clamp } from '../core/math';
import type { GeneratedLevel, PlayerState, RenderSettings, Vec2 } from '../core/types';
// The opaque surround an `immersive-ar` session needs and an `immersive-vr`
// session does not. Built lazily, and only for the sessions that need it.
import { makeXREnvironment, type XREnvironment } from './xrenv';
import { makeHandView, type XRHandView } from './xrhands';
// The session's quality tier and its frame-rate governor. It owns the
// eye-buffer scale (which three can only set before the session starts) and the
// comfort term in the post overlay, so the two files meet here.
import { setXRComfortMotion, xrPostGrade, xrQuality } from './xrquality';
import { applyIntentActions, makeXRInputs, readSticks, XRHeldActions, type XRAxes, type XRInputs } from './xrinput';
import type { XRIntent, XRIntentSet } from './xrintent';
import {
  defaultComfort,
  loadComfort,
  needsRecentre,
  saveComfort,
  settleBody,
  XRLocomotion,
  type XRComfort,
} from './xrlocomotion';
import { makeComfortVignette, type ComfortVignette } from './xrvignette';

/** Lowest believable eye height, metres. Guards against a bad first pose. */
const MIN_EYE = 0.3;

/**
 * Immersive modes to try, best first.
 *
 * `immersive-vr` is not merely preferred, it is the whole design: an opaque
 * compositor, a black world, nothing of the room. `immersive-ar` is here
 * because no headset in this house can be tested against and a device that
 * offers only the AR module would otherwise get nothing at all. What it costs
 * is the surround in `render/xrenv.ts` and a little fill rate.
 */
const MODES: readonly XRSessionMode[] = ['immersive-vr', 'immersive-ar'];

/**
 * Asked for, never depended on.
 *
 * `bounded-floor` is what a runtime uses to tell three the shape of the play
 * space; `hand-tracking` is what puts joints on the input sources, and on
 * visionOS it is the thing that raises a permission prompt at session start.
 * Both are given up whole on the second rung of the ladder rather than being
 * allowed to take the session down with them.
 */
const OPTIONAL_FEATURES: readonly string[] = ['bounded-floor', 'hand-tracking'];

/**
 * Eye height taken on faith when a runtime hands over a session but refuses
 * `local-floor`, metres.
 *
 * The floor plane is where the whole design measures from — standing height,
 * crouching, `PlayerState.eyeHeight` — and a `local` space has its origin
 * "near the viewer's position at the time the session was created", which is
 * head height rather than the floor. So the first pose of such a session is
 * *defined* to be about zero, and the correction is this number minus whatever
 * it actually reads.
 *
 * Written as a calibration rather than a constant offset on purpose. The spec
 * says head height; a runtime that decided to hand out floor-relative values
 * under the `local` name would, with a flat 1.6 m added, put the player's eyes
 * three metres up and their body looking down on the ceiling — much worse than
 * the problem being solved. Taking the difference is right either way and needs
 * nothing to be assumed about which kind of runtime this is.
 *
 * Only ever reached after every honest attempt has failed. No shipping WebXR
 * runtime is known to refuse `local-floor`.
 */
const ASSUMED_EYE = 1.6;

/** What one rung of the entry ladder asked for, and what it got. */
interface Granted {
  session: XRSession;
  /**
   * True when `local-floor` was in `requiredFeatures`, so the runtime granting
   * the session is itself the promise that the reference space exists. False
   * only on the bottom rung, where it has to be probed for.
   */
  floorGuaranteed: boolean;
}

/**
 * Whether a failed `requestSession` was a refusal rather than a mismatch.
 *
 * The distinction decides whether the ladder keeps climbing. A
 * `NotSupportedError` means "not like that, try another way" and is exactly
 * what the ladder is for. A `NotAllowedError` is the player or the system
 * saying no, and answering that by immediately asking for a *different* mode
 * would just put a second dialog in front of somebody who has already declined
 * one. `SecurityError` is a cross-origin frame or a permissions policy and no
 * amount of asking will change it.
 */
function refusal(err: unknown): 'hard' | 'soft' | null {
  const name = typeof err === 'object' && err !== null ? (err as { name?: unknown }).name : undefined;
  if (name === 'SecurityError') return 'hard';
  if (name === 'NotAllowedError') return 'soft';
  return null;
}

/**
 * Fraction of the player's own standing height below which they count as
 * crouching, and the height they have to come back up to before they stop.
 *
 * A fraction rather than a number of metres, because the player might be an
 * adult or might be eleven, and 0.95 m is a crouch for one and a slouch for the
 * other. Standing height is learned from the tallest the head has been.
 */
const CROUCH_ENTER = 0.78;
const CROUCH_EXIT = 0.86;

/**
 * How fast the learned standing height creeps back down, metres per second.
 *
 * It exists so that one spuriously tall pose — a headset held above the head
 * while it is being put on — cannot leave somebody counted as crouching for the
 * rest of the run. See `CROUCH_GRACE` for the thing it must not also do.
 */
const STANDING_DECAY = 0.02;

/**
 * Seconds a crouch is held before the standing estimate is allowed to creep
 * down through it.
 *
 * Without this the decay above ends a crouch nobody left. Measured: a head at
 * 70% of standing stops counting as crouched after 15.3 seconds of holding
 * perfectly still, and at that instant `PlayerState.noise` goes from 0.080 to
 * 0.400 against a hearing threshold of 0.22 — from inaudible to twice as loud
 * as the hound needs, with the player not having moved. Getting low is the
 * counterplay to the only entity that reliably kills, and a counterplay with a
 * fifteen-second timer on it that nothing tells you about is not one.
 *
 * Two minutes because that is longer than a hound: `TUNING.entities.surgeSpawn`
 * is sized for twenty to thirty seconds of approach and about ten of endgame,
 * and hiding through one and waiting for it to lose interest is under a minute.
 * After that the decay runs again, so a session that did open on a bad pose
 * still recovers — in a couple of minutes rather than never.
 */
const CROUCH_GRACE = 120;

/**
 * three's XR controller groups dispatch `connected`, `disconnected`,
 * `selectstart` and `selectend`, none of which are in the typed
 * `Object3DEventMap`. This is the narrow shape needed to listen for them.
 */
interface XRControllerEvents {
  addEventListener(type: string, listener: (event: { data?: XRInputSource }) => void): void;
  removeEventListener(type: string, listener: (event: { data?: XRInputSource }) => void): void;
}

/** The immersive session, as the rest of the game sees it. */
export interface XRPresenter {
  /**
   * True once the browser has said it can do at least one immersive mode.
   * False on every desktop and every phone, which is where it earns its keep.
   */
  readonly available: boolean;
  /** True between `sessionstart` and `sessionend`. */
  readonly presenting: boolean;
  /**
   * The mode the running session was actually granted, or null outside one.
   *
   * Worth reading because the two are not the same place: `immersive-vr` is
   * the design, and `immersive-ar` is the design with a painted-on sky.
   */
  readonly mode: XRSessionMode | null;
  /**
   * True when the compositor is blending the frame with the room the player is
   * standing in — and therefore when `render/xrenv.ts` is holding it out.
   */
  readonly passthrough: boolean;
  /**
   * True when the session granted hand tracking. Only ever advisory: nothing
   * may require it, because it is behind a permission prompt on visionOS and
   * behind a setting on Quest, and either can be a no.
   */
  readonly handTracking: boolean;
  /**
   * False while the headset is off the player's head or a system menu is over
   * the session. Frames keep arriving in that state but the poses stop moving,
   * so the simulation has to stop with them.
   */
  readonly visible: boolean;
  /** Comfort settings. Persisted here; see `xrlocomotion.ts`. */
  readonly comfort: Readonly<XRComfort>;
  setComfort(patch: Partial<XRComfort>): void;

  /**
   * What the player is pointing at and pressing, or null outside a session.
   *
   * The one seam through which anything above the render layer learns what an
   * XR player is asking for. `render/xrinteract.ts` picks the world off it and
   * `game.ts` turns what it picked back into supplies and stairways; neither
   * ever sees an `XRInputSource`. Null on every flat frame, which is what
   * leaves the desktop game with no XR code on its path at all.
   *
   * The intents themselves are reused in place by their producer: read them
   * within the frame and never retain one. See `xrintent.ts`.
   */
  readonly intents: XRIntentSet | null;
  /**
   * Say who owns a press, at the instant it happens.
   *
   * Locomotion and the world share one button, because a Vision Pro has exactly
   * one and there is no second gesture to give either of them. So the *target*
   * has to settle it, and it has to settle it in the same frame: a claim
   * published a frame late is no use on a pointer that exists for one frame.
   *
   * The function is called at the top of every frame, once per live intent,
   * and must answer without side effects. True means "this press is being spent
   * on something in the world or on a panel" — the press is then neither an
   * interact nor travel, and no hop is fired for it. Pass null to give the
   * question up, which is the state a session runs in until `game.ts` wires it.
   */
  setActionClaim(fn: ((intent: XRIntent) => boolean) | null): void;

  /**
   * Ask for a session. **Must be called from inside a user gesture**, like the
   * audio context — the two can share one. Resolves false when the browser
   * refused or there was nothing to enter, and a false leaves the flat game
   * exactly as it was.
   */
  enter(): Promise<boolean>;
  /** End the session. Safe to call when there is none. */
  exit(): void;
  /** What the "ENTER VR" affordance runs. `game.ts` supplies its own. */
  setEnterHandler(fn: () => void): void;
  /** Told when a session starts or ends. Returns an unsubscribe. */
  onChange(fn: (presenting: boolean) => void): () => void;

  /* -- The frame. None of this is reached outside a session. -- */

  /**
   * Read the headset and the controllers, turn the rig, feed the input sink,
   * and write the head's yaw and pitch into `PlayerState`.
   *
   * Runs at the very top of the frame, before the player controller, so the
   * direction the stick means is where the player is looking *now* rather than
   * where they were looking last frame.
   */
  beginFrame(dt: number, sink: InputSink, player: PlayerState): void;
  /**
   * Carry the body along with the rig, then put it back under the head.
   * Called immediately after the player controller has run.
   *
   * `locoDx/locoDz` is what the controller moved the body by this frame — stick
   * locomotion, and nothing else. The rig moves by exactly that, so the head
   * goes with it. Physical head movement then moves the *body*, the other way
   * round, which is `settleBody`'s job.
   *
   * Returns the metres `settleBody` moved the body by: the player's own legs,
   * which no other part of the game can see. `PlayerController.externalMotion`
   * wants it, because "standing still" has to mean standing still.
   */
  afterPlayer(player: PlayerState, level: GeneratedLevel, locoDx: number, locoDz: number, settle: boolean): number;
  /** Put the head over `pos` facing `yaw`. For a level load or a teleport. */
  recentre(pos: Vec2, yaw: number): void;
  /**
   * Take the picture away, 0..1.
   *
   * The descent's fade is a DOM element, and DOM does not render in a session,
   * so without this a headset gets the 100–400 ms generation freeze in full
   * light with nothing to explain it. Damped, but fast: this is a shutter, not
   * a mood.
   */
  setShutter(v: number): void;

  dispose(): void;
}

/** What the renderer additionally drives, and nothing outside it needs. */
export interface XRRuntime extends XRPresenter {
  /** The comfort tunnel, the hands and the torch. Called from `Renderer.update`. */
  update(dt: number): void;
  /** Body speed, for the tunnel. Physical walking is deliberately not in it. */
  trackPlayer(player: PlayerState): void;
  /** Show a torch in the hand once the player has actually found one. */
  setHolding(on: boolean): void;
}

/** What `xr.ts` needs from the renderer that built it. */
export interface XRHost {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /** `render/flashlight.ts`'s root, so the torch can move to a hand. */
  flashlight: THREE.Object3D;
  /**
   * Graphics quality, for the eye-buffer scale. Read once per entry, before
   * the session is requested — three cannot change it afterwards.
   */
  quality(): RenderSettings['quality'];
  /**
   * True when the post overlay is switched on in the player's settings — and
   * therefore when the comfort tunnel has a cheaper home than this file's
   * geometry. See `xrvignette.ts`.
   */
  postGrade(): boolean;
  /** Called on both edges, so the renderer can swap its own paths. */
  onPresenting(on: boolean): void;
}

/**
 * One of three's controller groups, and whatever is riding on it.
 *
 * This is *not* where input is read — `xrinput.ts` owns that, straight off the
 * session, for the reasons its header gives. What is left here is the half
 * three is genuinely the authority on: the visible hand, the pointer stub, and
 * the group the torch is parented to. All this needs to know about the source
 * is that there is one and which hand it is on, so the torch has somewhere to
 * go.
 */
interface Slot {
  view: XRHandView;
  source: XRInputSource | null;
  /**
   * Takes this slot's listeners back off three's controller group.
   *
   * three caches its `WebXRController`s for the life of the renderer and hands
   * the same `Group`s back to the next session, so listeners added on entry and
   * never removed would stack one set per session — each one still holding the
   * dead slot it closed over.
   */
  off(): void;
}

class Presenter implements XRRuntime {
  available = false;
  presenting = false;
  visible = true;
  mode: XRSessionMode | null = null;
  passthrough = false;
  handTracking = false;

  /** What `isSessionSupported` answered at boot, per mode. */
  private readonly supported = new Set<XRSessionMode>();

  private readonly host: XRHost;
  private readonly rig = new THREE.Group();
  private readonly loco = new XRLocomotion();
  /**
   * Built on the first session and never on a flat machine. It is a sphere and
   * a shader; small, but a desktop that will never wear a headset should not be
   * carrying either of them.
   */
  private vignette: ComfortVignette | null = null;
  /**
   * The opaque surround. Built on the first session that turns out to composite
   * against the real room, and never on one that does not — a Quest pays
   * nothing for it, and a desktop never so much as imports its geometry.
   */
  private env: XREnvironment | null = null;
  /**
   * The defaults until a device says it can present, at which point the
   * player's saved comfort settings are read. Same reasoning: a desktop should
   * not touch storage for a feature it does not have.
   */
  private comfortState: XRComfort = defaultComfort();

  private session: XRSession | null = null;
  /**
   * True from the moment a session is asked for until it is running or has
   * failed.
   *
   * `session` is only set once `begin` has finished, and starting a session is
   * several awaits long — the request itself, `makeXRCompatible`, the layer,
   * the reference space. Guarding on `session` alone therefore lets a second
   * attempt in during all of that: two `requestSession` calls, two sets of
   * hands built and one of them orphaned, and a rig added twice. That is a
   * double tap on ENTER VR, or a `sessiongranted` arriving from the headset's
   * browser while a click is already in flight, which is exactly when it
   * happens.
   */
  private entering = false;
  /**
   * Set while entering when a request came back as a refusal rather than as a
   * mismatch, so the ladder stops instead of putting a second permission dialog
   * in front of somebody who has just dismissed one. Cleared when `enter`
   * finishes, whatever the outcome.
   */
  private declined = false;
  private slots: Slot[] = [];
  private torchSlot = -1;
  /**
   * The sink the controllers have been feeding. Held only so that a session
   * ending with the trigger down does not leave the flat game permanently
   * interacting — `hold` is latched until it is set again.
   */
  private sink: InputSink | null = null;

  /**
   * What the hardware in the room is asking for, or null outside a session.
   *
   * Built once per session and disposed with it. Everything downstream of the
   * frame loop — locomotion, `render/xrinteract.ts`, the in-world panels —
   * reads this and never touches an `XRInputSource`; see `xrintent.ts` for why
   * that seam exists at all.
   */
  private inputs: XRInputs | null = null;
  /**
   * Asked, at the top of every frame, whether something other than locomotion
   * owns the press that is happening. See `setActionClaim`.
   */
  private claim: ((intent: XRIntent) => boolean) | null = null;
  /** This frame's answer, latched so both halves of the frame agree on it. */
  private claimed = false;
  /**
   * This frame's `dt`, latched in `beginFrame`.
   *
   * `afterPlayer` needs it — teleport bills a speed budget in seconds and its
   * fade is a level rather than an event — and the signature it is called
   * through does not carry one.
   */
  private frameDt = 0;
  private readonly axes: XRAxes = { moveX: 0, moveZ: 0, turn: 0 };
  private readonly held = new XRHeldActions();
  private readonly listeners = new Set<(presenting: boolean) => void>();
  private enterHandler: () => void = () => void this.enter();
  private button: HTMLButtonElement | null = null;

  /**
   * Metres the rig is lifted, and the head's Y is lifted with it, to stand in
   * for a floor plane the runtime would not give us. Zero in every session that
   * granted `local-floor`, which should be all of them. See `ASSUMED_EYE`.
   */
  private floorOffset = 0;
  /** True until the floorless fallback has seen a pose to calibrate against. */
  private floorPending = false;

  /** Rig yaw, three's convention: increasing it turns the view left. */
  private rigYaw = 0;
  /** Head pose in rig space, refreshed at the top of every frame. */
  private headX = 0;
  private headY = 1.6;
  private headZ = 0;
  private headYaw = 0;
  private headPitch = 0;
  /** Head position in world space, derived from the rig. */
  private worldX = 0;
  private worldZ = 0;

  /**
   * Tallest the head has been, for the crouch test.
   *
   * Seeded from the session's own first pose rather than from a number, which
   * is the only way it can be right for a player who is not an adult standing
   * up. Left over from a previous session it would be somebody else's height;
   * `begin` puts it back to the floor of believability and the first frame
   * raises it. Measured before that: a 1.15 m eye height — a small child, or
   * anybody sitting down — read as crouching from the first frame of every
   * session and stayed that way for thirteen seconds.
   */
  private standing = MIN_EYE;
  private crouched = false;
  /** Seconds the crouch has been held, for `CROUCH_GRACE`. */
  private crouchedFor = 0;

  /** Driven from the renderer, spent on the comfort tunnel. */
  private speed = 0;
  /** How far into a wall the head has been pushed, 0..1. */
  private wallBlack = 0;
  /** The descent's veil, which in here is geometry rather than a DOM element. */
  private shutter = 0;

  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');

  constructor(host: XRHost) {
    this.host = host;
    this.rig.name = 'xr-rig';
    this.probe();
  }

  get comfort(): Readonly<XRComfort> {
    return this.comfortState;
  }

  get intents(): XRIntentSet | null {
    return this.inputs;
  }

  setActionClaim(fn: ((intent: XRIntent) => boolean) | null): void {
    this.claim = fn;
  }

  setComfort(patch: Partial<XRComfort>): void {
    this.comfortState = { ...this.comfortState, ...patch };
    this.loco.setComfort(this.comfortState);
    saveComfort(this.comfortState);
    // The torch may have changed hands; let the next frame work it out again.
    this.torchSlot = -1;
  }

  setEnterHandler(fn: () => void): void {
    this.enterHandler = fn;
  }

  onChange(fn: (presenting: boolean) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /* ---------------------------------------------------------------- *
   * Support, and the way in
   * ---------------------------------------------------------------- */

  /**
   * Ask once, at boot, whether there is anything to enter.
   *
   * Nothing at all happens on a browser without `navigator.xr`, and nothing
   * happens on one that has it but has no headset attached — which is every
   * desktop this game is played on. The affordance is only built when some mode
   * answered yes, so the question of *which* device this is never has to be
   * guessed from a user agent string.
   *
   * Both modes are asked in parallel and both are allowed to fail: a browser
   * that has `navigator.xr` but has never heard of `immersive-ar` may reject
   * rather than resolve false, and one refusal must not lose the other answer.
   */
  private probe(): void {
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr;
    if (!xr || typeof xr.isSessionSupported !== 'function') return;
    const ask = (mode: XRSessionMode): Promise<boolean> => {
      try {
        return xr.isSessionSupported(mode).then(
          (ok) => ok === true,
          () => false
        );
      } catch {
        // A synchronous throw, which some older implementations do in a
        // cross-origin frame rather than returning a rejected promise.
        return Promise.resolve(false);
      }
    };
    void Promise.all(MODES.map(ask)).then((answers) => {
      for (let i = 0; i < MODES.length; i++) {
        if (answers[i]) this.supported.add(MODES[i]!);
      }
      if (this.supported.size === 0) return;
      this.available = true;
      this.comfortState = loadComfort();
      this.loco.setComfort(this.comfortState);
      this.mountButton();
      // A headset's own browser can hand a page a session without a click —
      // "enter VR" from the address bar. Treated as the gesture it is.
      (xr as EventTarget).addEventListener('sessiongranted', this.onGranted);
    });
  }

  private onGranted = (): void => {
    this.enterHandler();
  };

  /**
   * The affordance, built by hand rather than through `ui/`.
   *
   * It exists only on a device that answered yes, so a desktop page never gains
   * an element or a listener. It carries no inline styles: `.xr-enter` in
   * `ui/styles.css` is the whole of its appearance, which is the only
   * arrangement under which a later rule there can actually change it — an
   * inline declaration beats every selector that is not `!important`, so the
   * styles this used to set itself could not have been overridden at all.
   */
  private mountButton(): void {
    if (this.button || typeof document === 'undefined') return;
    const b = document.createElement('button');
    b.className = 'xr-enter';
    b.type = 'button';
    // What this device actually offered. A headset that answered yes only to
    // `immersive-ar` is going to get a session with the room composited behind
    // the building — see `xrenv.ts`, which shuts it out — and telling that
    // player they are entering VR is a promise about their next ten seconds
    // that the hardware has already refused to keep.
    b.textContent = this.supported.has('immersive-vr') ? 'ENTER VR' : 'ENTER AR';
    b.addEventListener('click', () => this.enterHandler());
    document.body.appendChild(b);
    this.button = b;
  }

  async enter(): Promise<boolean> {
    if (!this.available || this.session || this.entering) return false;
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr;
    if (!xr) return false;
    this.entering = true;
    try {
      // Before the request, and only here: `WebXRManager` reads the eye-buffer
      // scale when it builds the session's layer and cannot be told again.
      xrQuality.prepare(this.host.renderer, this.host.quality());
      // A previous session may have settled for a floorless space. Ask properly
      // again: whatever refused last time may have been a permission, and the
      // player may since have said yes.
      this.host.renderer.xr.setReferenceSpaceType('local-floor');
      this.floorOffset = 0;
      this.floorPending = false;

      // The first `requestSession` below runs with nothing awaited in front of
      // it, so it is still inside the click that started this. Everything after
      // the first rung is already outside the gesture and is only reached when
      // the gesture bought nothing.
      for (const mode of MODES) {
        if (!this.supported.has(mode)) continue;
        const granted = await this.request(xr, mode);
        if (granted) {
          await this.begin(granted, mode);
          return true;
        }
        if (this.declined) break;
      }
      return false;
    } catch {
      // Refused, cancelled, or no device after all. The flat game is untouched
      // and the player is still looking at it.
      return false;
    } finally {
      this.entering = false;
      this.declined = false;
    }
  }

  /**
   * One mode, up to three ways.
   *
   * The rungs, in order, and why each one exists:
   *
   *  1. Everything. `local-floor` required because the design measures from the
   *     floor, plus the optional features that make a session nicer.
   *  2. The same, without the optional features. A runtime that has never heard
   *     of a feature string is supposed to ignore it in `optionalFeatures`, and
   *     they do not all do that; and on visionOS `hand-tracking` is the feature
   *     that raises a prompt. Being told no to hands must cost the hands and
   *     nothing else, so this rung asks for the session on its own.
   *  3. Nothing required at all. The floor plane becomes a guess. See
   *     `ASSUMED_EYE` — this is the rung that should never fire.
   */
  private async request(xr: XRSystem, mode: XRSessionMode): Promise<Granted | null> {
    const inits: readonly XRSessionInit[] = [
      { requiredFeatures: ['local-floor'], optionalFeatures: [...OPTIONAL_FEATURES] },
      { requiredFeatures: ['local-floor'] },
      { optionalFeatures: ['local-floor', ...OPTIONAL_FEATURES] },
    ];
    let declines = 0;
    for (let i = 0; i < inits.length; i++) {
      try {
        const session = await xr.requestSession(mode, inits[i]!);
        return { session, floorGuaranteed: i < inits.length - 1 };
      } catch (err) {
        const kind = refusal(err);
        if (kind === 'hard') {
          this.declined = true;
          return null;
        }
        if (kind === 'soft') {
          this.declined = true;
          // One more ask, in case what was declined was a prompt one of the
          // optional features raised rather than the session itself. Never a
          // third: past that it is a person being asked to say no repeatedly.
          if (++declines > 1) return null;
        }
      }
    }
    return null;
  }

  exit(): void {
    void this.session?.end();
  }

  /* ---------------------------------------------------------------- *
   * Session lifecycle
   * ---------------------------------------------------------------- */

  private async begin(granted: Granted, mode: XRSessionMode): Promise<void> {
    const session = granted.session;
    const renderer = this.host.renderer;
    renderer.xr.enabled = true;

    // Only the bottom rung of the ladder can arrive here without a promise of a
    // floor plane, and only that rung pays for the check. Asking for the space
    // and throwing it away is the one reliable test: `enabledFeatures` is
    // optional in the spec and absent on runtimes old enough to be the ones
    // this rung exists for. three requests its own space inside `setSession`,
    // and a rejection there would take the whole session down — so the answer
    // has to be known before that call, not after it.
    if (!granted.floorGuaranteed) {
      try {
        await session.requestReferenceSpace('local-floor');
      } catch {
        renderer.xr.setReferenceSpaceType('local');
        // The offset cannot be known yet: it is worked out from the first pose,
        // in `readHead`. Until then the rig sits at zero and nothing has moved.
        this.floorPending = true;
      }
    }

    this.loco.reset();
    // Whose head this is, learned from scratch. A height carried over from a
    // previous session belongs to whoever was wearing it then.
    this.standing = MIN_EYE;
    this.crouched = false;
    this.crouchedFor = 0;
    this.wallBlack = 0;
    this.shutter = 0;
    this.rigYaw = 0;
    this.rig.rotation.set(0, 0, 0);
    this.rig.position.set(0, this.floorOffset, 0);
    this.host.scene.add(this.rig);
    // Before `setSession`, and this ordering is load-bearing. three assigns
    // each incoming input source to the first free entry of its own
    // `controllers` array, and that array is *only* populated by
    // `getController`/`getControllerGrip`. `inputsourceschange` fires for the
    // initial set, and if it arrives while the array is still empty three
    // drops the source and never reconsiders it — a session with no hands at
    // all. Both groups are invisible until a pose arrives, so putting them in
    // the scene a moment early costs nothing and shows nothing.
    this.buildHands(renderer);

    try {
      // What actually starts XR rendering, and what swaps the window's
      // animation loop for the session's. It can reject — a lost device, a
      // refused reference space — and a rejection has to leave nothing behind.
      await renderer.xr.setSession(session);
    } catch (err) {
      this.teardownHands();
      this.host.scene.remove(this.rig);
      renderer.xr.enabled = false;
      this.floorOffset = 0;
      this.floorPending = false;
      // The session itself is still alive — the runtime granted it and only
      // three's setup failed — so without this the headset is left holding an
      // immersive session that nothing is drawing into and the player has no
      // way back out of except the system menu.
      void session.end().catch(() => {});
      throw err;
    }

    // Fixed foveation. The periphery of a lens-corrected eye buffer is the
    // cheapest thing in the frame to give away and the hardest to notice; the
    // governor in `xrquality.ts` treats this as a floor it may climb from.
    renderer.xr.setFoveation(TUNING.render.xr.foveation);

    this.session = session;
    this.mode = mode;
    this.visible = session.visibilityState === 'visible';
    // The rig, because that is the transform that maps the session's reference
    // space onto the building: rays come out of here already in world space and
    // nothing downstream has to know a reference space exists. After
    // `setSession`, so a rejected session never leaves listeners on a dead one.
    this.inputs = makeXRInputs(session, this.rig);
    // The arc's parabola and landing ring go in the **scene**, not in the rig.
    // It marks a place in the building, and a marker parented to the play space
    // would slide out from under the player on every snap turn.
    this.host.scene.add(this.loco.aimView());
    // What the runtime actually granted, which is not what was asked for.
    const features = session.enabledFeatures;
    this.handTracking = Array.isArray(features) ? features.includes('hand-tracking') : false;
    session.addEventListener('end', this.onEnd);
    (session as EventTarget).addEventListener('visibilitychange', this.onVisibility);

    // The camera moves into the rig. three composes the head pose with the
    // parent's world matrix, which is what makes the rig mean anything at all.
    this.rig.add(this.host.camera);
    this.vignette = this.vignette ?? makeComfortVignette();
    this.host.camera.add(this.vignette.object);
    this.mountEnvironment(session, mode);

    this.presenting = true;
    this.host.onPresenting(true);
    for (const fn of this.listeners) fn(true);
  }

  /**
   * Shut the room out, if there is a room to shut out.
   *
   * `environmentBlendMode` is the only honest way to ask, and it is the session
   * that answers rather than the mode that was requested: a headset may perfectly
   * well hand back an `immersive-vr` session that composites additively. Where
   * it is missing entirely — an implementation older than that part of the spec
   * — the mode is the fallback guess, and the guess errs toward drawing the
   * surround, because a redundant backdrop costs a fill and a missing one costs
   * the whole premise.
   *
   * `additive` is a lost cause and is treated as passthrough anyway. On an
   * additive display black *is* transparent, so nothing drawn can occlude the
   * room; the fog colour at least puts a haze over it. No such device is a
   * target here.
   */
  private mountEnvironment(session: XRSession, mode: XRSessionMode): void {
    // The cast is the point: `@types/webxr` declares this as always present,
    // and an implementation predating the AR module does not have it at all.
    const declared = (session as { environmentBlendMode?: XREnvironmentBlendMode }).environmentBlendMode;
    const blend = declared ?? (mode === 'immersive-ar' ? 'alpha-blend' : 'opaque');
    this.passthrough = blend !== 'opaque';
    if (!this.passthrough) return;
    this.env = this.env ?? makeXREnvironment();
    this.env.syncTo(this.host.scene);
    this.host.camera.add(this.env.object);
  }

  private buildHands(renderer: THREE.WebGLRenderer): void {
    this.teardownHands();
    for (let i = 0; i < 2; i++) {
      const view = makeHandView(renderer, i);
      const slot: Slot = { view, source: null, off: () => {} };
      const ev = view.ray as unknown as XRControllerEvents;
      // Only which hand this group is standing in for, so the torch can find
      // it. `selectstart` and `selectend` are deliberately *not* listened for
      // here: three only routes them once it has assigned the source one of its
      // two slots, and a transient pointer is created, fired and destroyed
      // inside one gesture. `xrinput.ts` takes them off the session instead.
      const onConnected = (e: { data?: XRInputSource }): void => {
        slot.source = e.data ?? null;
      };
      const onDisconnected = (): void => {
        slot.source = null;
      };
      ev.addEventListener('connected', onConnected);
      ev.addEventListener('disconnected', onDisconnected);
      slot.off = (): void => {
        ev.removeEventListener('connected', onConnected);
        ev.removeEventListener('disconnected', onDisconnected);
      };
      this.rig.add(view.ray, view.grip);
      this.slots.push(slot);
    }
    this.torchSlot = -1;
  }

  private teardownHands(): void {
    for (const slot of this.slots) {
      slot.off();
      this.rig.remove(slot.view.ray, slot.view.grip);
      slot.view.dispose();
    }
    this.slots = [];
    this.torchSlot = -1;
  }

  private onEnd = (): void => {
    if (this.session) {
      (this.session as EventTarget).removeEventListener('visibilitychange', this.onVisibility);
    }
    this.session = null;
    this.presenting = false;
    this.visible = true;
    this.mode = null;
    this.passthrough = false;
    this.handTracking = false;
    this.floorOffset = 0;
    this.floorPending = false;
    // Before anything else: `InputSink.hold` latches, so a trigger that was
    // down when the headset came off would stay down in the flat game.
    if (this.sink) this.held.releaseAll(this.sink);
    this.sink = null;
    // Takes the three session listeners back off. After `releaseAll`, so an
    // action that was down is let go of through the sink rather than simply
    // stopping being reported.
    this.inputs?.dispose();
    this.inputs = null;
    this.claimed = false;
    this.host.scene.remove(this.loco.aimView());

    // The torch comes off the hand before the hands are taken away.
    this.mountTorch(-1);
    if (this.vignette) this.host.camera.remove(this.vignette.object);
    // Off the camera, not disposed: the next session may well need it again,
    // and a box and a colour are not worth rebuilding. A session that turns out
    // to be opaque simply never puts it back.
    if (this.env) this.host.camera.remove(this.env.object);
    this.teardownHands();
    // Back into the scene, where `syncCamera` writes the camera directly again.
    // three has already restored the canvas size and the pixel ratio; the
    // renderer puts the field of view and the aspect back, because three
    // overwrites both from the headset's projection.
    this.host.scene.add(this.host.camera);
    this.host.scene.remove(this.rig);
    this.host.renderer.xr.enabled = false;

    this.host.onPresenting(false);
    for (const fn of this.listeners) fn(false);
  };

  /**
   * The headset coming off, or a system menu opening over the session.
   *
   * Frames keep arriving while the state is `visible-blurred` but the poses do
   * not move, so a player who takes the headset off mid-run would otherwise
   * come back to whatever had walked up to them while they were not looking.
   */
  private onVisibility = (): void => {
    this.visible = this.session?.visibilityState === 'visible';
  };

  /* ---------------------------------------------------------------- *
   * The frame
   * ---------------------------------------------------------------- */

  beginFrame(dt: number, sink: InputSink, player: PlayerState): void {
    if (!this.session) return;
    this.sink = sink;
    this.frameDt = dt;
    this.readHead();
    this.readInputs(dt);

    const inputs = this.inputs;
    if (inputs) {
      readSticks(inputs, this.comfortState.moveHand, this.axes);
      // A runtime with no thumbsticks at all — visionOS hands out a transient
      // pointer per pinch and nothing else — turns by holding a pinch on the
      // hand that is not steering. Added to the stick rather than replacing it,
      // so a Quest player who has put one controller down still turns with the
      // other, and clamped because the two could otherwise sum past full.
      // Sticks only. `pinchTurn` returns 0 on a stickless headset, where you
      // turn by turning — see the note on it.
      this.axes.turn = clamp(this.axes.turn + this.loco.pinchTurn(inputs), -1, 1);
      // Under teleport the body does not walk, and pushing the stick into the
      // sink as well would move it twice — once by the hop and once by the
      // controller. Cleared rather than skipped: `axes` latches in the input
      // manager exactly as `hold` does.
      if (this.loco.teleporting) {
        this.axes.moveX = 0;
        this.axes.moveZ = 0;
      }
      // Written every frame either way. An action latched down and then simply
      // no longer written stays down in the flat game forever, which is the
      // failure `XRHeldActions` exists to prevent.
      applyIntentActions(inputs, sink, this.held, !this.claimed && !this.loco.claimsPress(inputs));
    } else {
      // Unreachable while a session is running — `begin` builds the producer
      // before the first frame — but the axes latch, so leaving last frame's
      // values in them would be a stick stuck on.
      this.axes.moveX = 0;
      this.axes.moveZ = 0;
      this.axes.turn = 0;
    }
    sink.axes(this.axes.moveX, this.axes.moveZ);

    const turn = this.loco.turn(this.axes.turn, dt);
    if (turn.delta !== 0) this.turnRig(turn.delta);

    // Crouch is a stance the player takes with their own knees. Reported as the
    // `crouch` action so that `player/controller.ts` reaches exactly the state
    // it would from the keyboard — which is what makes `hidden`, and therefore
    // the hound's counterplay, work in here at all.
    this.updateStance(dt);
    this.held.set(sink, 'crouch', this.crouched);

    player.yaw = -(this.rigYaw + this.headYaw);
    player.pitch = this.headPitch;
  }

  afterPlayer(
    player: PlayerState,
    level: GeneratedLevel,
    locoDx: number,
    locoDz: number,
    settle: boolean
  ): number {
    if (!this.session) return 0;
    // Travel. `settle` is already almost exactly the right gate — false when
    // the player is frozen and false when the simulation is halted — and the
    // claim is the rest of it: a pinch spent on a menu row or on a granola bar
    // must not also send the player across the room. Called on every frame of a
    // session, including the ones where nothing may move, because the hop's
    // fade is a level rather than an event and a caller that stopped speaking
    // would leave the picture dark.
    const hop = this.loco.move(this.inputs, player, level, this.frameDt, settle && !this.claimed);
    // The rig carries the body's stick movement and its hop, so the head goes
    // with the body either way.
    this.rig.position.x += locoDx + hop.dx;
    this.rig.position.z += locoDz + hop.dz;
    this.projectHead();

    // Before `settleBody`, so what the player's *legs* did can be separated from
    // what the stick and the hop did. A recentre is a level load rather than a
    // walk and is deliberately not counted.
    const wasX = player.pos.x;
    const wasZ = player.pos.z;
    // The hop is in here with the legs, and for the same reason: neither is
    // movement `PlayerController` made, so `PlayerState.speed` is zero through
    // both and nothing downstream would otherwise hear either of them. It is
    // what makes a dashing player audible — see `PlayerController.externalMotion`.
    let walked = hop.distance;

    if (settle) {
      if (needsRecentre(player, this.worldX, this.worldZ)) {
        this.recentre(player.pos, player.yaw);
      } else {
        this.wallBlack = settleBody(player, level, this.worldX, this.worldZ);
        walked += Math.hypot(player.pos.x - wasX, player.pos.z - wasZ);
      }
    } else {
      this.wallBlack = 0;
    }

    // The headset is the authority on where the eyes are, which is what makes
    // ducking behind a desk a real thing to do rather than a keypress.
    player.eyeHeight = Math.max(MIN_EYE, this.headY);
    return walked;
  }

  recentre(pos: Vec2, yaw: number): void {
    if (!this.session) return;
    // `renderYaw` in `core/math.ts` is the whole conversion between the game's
    // yaw and three's; the head's own yaw inside the rig is then subtracted so
    // the total comes out at the facing the caller asked for.
    this.rigYaw = -yaw - this.headYaw;
    this.rig.rotation.y = this.rigYaw;
    const s = Math.sin(this.rigYaw);
    const c = Math.cos(this.rigYaw);
    this.rig.position.x = pos.x - (this.headX * c + this.headZ * s);
    this.rig.position.z = pos.z - (-this.headX * s + this.headZ * c);
    this.projectHead();
    this.loco.reset();
    this.wallBlack = 0;
  }

  update(dt: number): void {
    const vignette = this.vignette;
    if (!this.session || !vignette) return;
    this.mountTorch(this.findTorchSlot());
    // The backdrop tracks the level's fog, and the level changes underneath a
    // session that outlives several descents. Only ever built in a passthrough
    // session, so on a Quest this is one null test a frame.
    this.env?.syncTo(this.host.scene);

    // The comfort tunnel lives in the post overlay when there is one — it is a
    // triangle the frame is already drawing, and `render/postfx.ts` folds it in
    // with the vignette it was drawing anyway. The geometric shutter only takes
    // the tunnel back when the grade is unavailable, because comfort must not
    // be something a graphics setting can switch off.
    const aperture = this.loco.aperture(this.speed, dt);
    const graded = this.host.postGrade() && xrPostGrade();
    setXRComfortMotion(graded ? aperture : 0);
    vignette.setAperture(graded ? 0 : aperture);
    vignette.setBlackout(Math.max(this.loco.blackout(), this.wallBlack, this.shutter));
    vignette.update(dt);
    // The rig's world matrix has to be current before the renderer culls off
    // the camera, and three only recomposes the camera's own matrices inside
    // `render()`. The rig's parent is the scene, whose transform is identity.
    this.rig.updateMatrix();
    this.rig.matrixWorld.copy(this.rig.matrix);
  }

  setShutter(v: number): void {
    this.shutter = v;
  }

  trackPlayer(player: PlayerState): void {
    this.speed = player.speed;
  }

  setHolding(on: boolean): void {
    for (let i = 0; i < this.slots.length; i++) {
      this.slots[i].view.setHolding(on && i === this.torchSlot);
    }
  }

  dispose(): void {
    if (this.presenting) this.onEnd();
    this.vignette?.dispose();
    this.vignette = null;
    this.env?.dispose();
    this.env = null;
    // The arc's geometry. A session ending does not need this — the next one
    // will want the same curve back — but a teardown does.
    this.loco.dispose();
    this.claim = null;
    this.button?.remove();
    this.button = null;
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr;
    if (xr) (xr as EventTarget).removeEventListener('sessiongranted', this.onGranted);
    this.listeners.clear();
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * The head, in rig space.
   *
   * three's `WebXRManager` computes the viewer pose and decomposes it onto the
   * XR camera *before* it calls the animation-loop callback, so by the time
   * this runs the numbers are already this frame's. `matrixWorld` is not — that
   * is only composed inside `render()` — which is precisely why the rig-local
   * pose is what gets read and the world position is derived here by hand.
   */
  private readHead(): void {
    const cam = this.host.renderer.xr.getCamera();
    // Where the floor is, if the runtime would not say. The first pose of a
    // `local` session is by definition about where the viewer was when it
    // started, so the difference between that and a standing eye height is the
    // correction — and it comes out near zero rather than near 1.6 if the
    // runtime turns out to have been measuring from the floor all along.
    // Never negative: sinking the player into the ground is worse than leaving
    // a generous runtime's numbers alone.
    if (this.floorPending) {
      this.floorPending = false;
      this.floorOffset = Math.max(0, ASSUMED_EYE - cam.position.y);
      this.rig.position.y = this.floorOffset;
    }
    this.headX = cam.position.x;
    // Height above the floor, which is what everything downstream means by it.
    // `floorOffset` is zero in a `local-floor` session, where the reference
    // space already puts the floor at zero. The rig carries the same offset, so
    // the head's world position and this stay the same number.
    this.headY = cam.position.y + this.floorOffset;
    this.headZ = cam.position.z;
    this.euler.setFromQuaternion(cam.quaternion, 'YXZ');
    this.headYaw = this.euler.y;
    this.headPitch = this.euler.x;
    // Carried onto the game's own camera so this frame's chunk culling happens
    // from where the head is now. three writes the same values back in
    // `render()`; this only moves them earlier. Rig-*local*, deliberately: the
    // camera is a child of the rig, so adding `floorOffset` here as well as to
    // the rig would count it twice.
    this.host.camera.position.set(this.headX, cam.position.y, this.headZ);
    this.host.camera.quaternion.copy(cam.quaternion);
    this.projectHead();
  }

  /**
   * Turn, about the player's own head rather than about the rig's origin.
   *
   * This is the difference between a snap turn and a snap *step*. The rig
   * origin is wherever the play space was centred when the level loaded, and
   * the head is wherever the player has since wandered inside it — half a metre
   * away in a chair, a couple of metres in a cleared room. Rotating the rig
   * about its own origin swings the head around that circle, and because the
   * body follows the head (`settleBody`), a 30° snap would slide the player
   * sideways through the building every time they turned.
   *
   * So the yaw is applied and then the rig is translated by exactly the amount
   * that puts the head back where it was. Two projections rather than a matrix:
   * it happens once per snap, and the algebra is legible this way.
   */
  private turnRig(delta: number): void {
    const beforeX = this.worldX;
    const beforeZ = this.worldZ;
    this.rigYaw += delta;
    this.rig.rotation.y = this.rigYaw;
    this.projectHead();
    this.rig.position.x += beforeX - this.worldX;
    this.rig.position.z += beforeZ - this.worldZ;
    this.projectHead();
  }

  /** Head position in world space, from the rig. */
  private projectHead(): void {
    const s = Math.sin(this.rigYaw);
    const c = Math.cos(this.rigYaw);
    this.worldX = this.rig.position.x + (this.headX * c + this.headZ * s);
    this.worldZ = this.rig.position.z + (-this.headX * s + this.headZ * c);
  }

  /**
   * Crouching, measured against the player's own height rather than against a
   * number. The tallest the head has been is taken as standing, seeded from
   * this session's first pose, and it creeps back down slowly so that one bad
   * pose cannot leave somebody counted as crouching for the rest of the run.
   *
   * The creep is held while the player is actually down there, which is the
   * whole of `CROUCH_GRACE`. Without it the two rules fight and the wrong one
   * wins: a crouch held still ends itself, on a timer, in fifteen seconds.
   */
  private updateStance(dt: number): void {
    if (this.headY > this.standing) {
      this.standing = this.headY;
      this.crouchedFor = 0;
    } else if (!this.crouched || this.crouchedFor > CROUCH_GRACE) {
      // Only while the player is not down there on purpose. See `CROUCH_GRACE`.
      this.standing = Math.max(MIN_EYE, this.standing - STANDING_DECAY * dt);
    }
    const k = this.headY / Math.max(MIN_EYE, this.standing);
    this.crouched = this.crouched ? k < CROUCH_EXIT : k < CROUCH_ENTER;
    this.crouchedFor = this.crouched ? this.crouchedFor + dt : 0;
  }

  /**
   * This frame's intents, and who owns the press in them.
   *
   * `getFrame()` is null until the session's first animation frame has begun
   * and `getReferenceSpace()` is null until three has one, so both guards are
   * load-bearing rather than defensive — a session's very first callback
   * reaches here with neither.
   *
   * The claim is asked once per live intent and latched for the frame, so that
   * the top of the frame (which decides whether a press is an `interact`) and
   * `afterPlayer` (which decides whether it is a hop) can never disagree about
   * the same press.
   */
  private readInputs(dt: number): void {
    const inputs = this.inputs;
    this.claimed = false;
    if (!inputs) return;
    const xr = this.host.renderer.xr;
    const frame = xr.getFrame();
    const refSpace = xr.getReferenceSpace();
    if (frame && refSpace) inputs.update(frame, refSpace, dt);

    const claim = this.claim;
    if (!claim) return;
    const all = inputs.all;
    for (let i = 0; i < all.length; i++) {
      const intent = all[i]!;
      if (!intent.acting) continue;
      if (claim(intent)) {
        this.claimed = true;
        return;
      }
    }
  }

  /**
   * Which slot is the torch hand, or -1 for "nobody, put it back on the head".
   *
   * Presence is read off three's own controller group rather than off the
   * intents: this is a question about where a *mesh* can be parented, and three
   * is the authority on that. A transient pointer never reaches here at all,
   * which is correct — there is no hand to hang a torch on.
   */
  private findTorchSlot(): number {
    const want = this.comfortState.torchHand;
    for (let i = 0; i < this.slots.length; i++) {
      if (this.handOfSlot(i) === want) return i;
    }
    // A runtime that reports no handedness at all still gets a torch: the first
    // hand it has. Better in the wrong hand than back on the forehead.
    for (let i = 0; i < this.slots.length; i++) {
      if (this.handOfSlot(i) !== null) return i;
    }
    return -1;
  }

  /** Which hand a slot is tracking, or null when it is tracking nothing. */
  private handOfSlot(i: number): 'left' | 'right' | 'none' | null {
    const slot = this.slots[i];
    const src = slot ? slot.source : null;
    if (!slot || !src || !slot.view.ray.visible) return null;
    return src.handedness === 'left' || src.handedness === 'right' ? src.handedness : 'none';
  }

  /**
   * Move the torch. A flashlight held in a hand is a far better object in VR
   * than one welded to your face: it is the difference between looking at a
   * corridor and lighting it, and it is the first thing anybody tries.
   *
   * It hangs off the *target ray* space rather than the grip, because the ray
   * is the only one of the two the WebXR spec pins down exactly — it always
   * runs along -Z — and a torch that shines somewhere other than where it is
   * pointed is worse than no torch at all.
   */
  private mountTorch(slot: number): void {
    if (slot === this.torchSlot) return;
    this.torchSlot = slot;
    const light = this.host.flashlight;
    light.parent?.remove(light);
    if (slot >= 0 && this.slots[slot]) this.slots[slot].view.mount.add(light);
    else this.host.camera.add(light);
  }
}

/**
 * Build the XR presenter. Cheap and inert: one feature test and one promise
 * that never resolves true on a machine with no headset. `render/renderer.ts`
 * calls this once at boot.
 */
export function makeXR(host: XRHost): XRRuntime {
  return new Presenter(host);
}
